import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Plus,
  Building,
  Loader2,
  FilePen,
} from "lucide-react";
import { ManagementCompany, ManagementCompanySearchParams } from "@/models/ManagementCompany";
import { useToast } from "@/hooks/use-toast";
import { PaginationControls } from "@/components/shared/PaginationControls";
import { managementCompanyApi } from "@/services/api/organizationApi";
import { useAuth } from "@/contexts/AuthContext";

const ManagementCompanies = () => {
  const { toast } = useToast();
  const { selectedRole, userRoles, permissionsLoaded, hasPermission, getCurrentRoleIds } = useAuth();
  
  const [searchParams, setSearchParams] = useState<ManagementCompanySearchParams>({
    search: "",
    limit: 10,
    offset: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Simple filtering logic - backend now handles role-based filtering
  const getApiFilters = () => {
    const filters: any = {};
    // Backend will handle role-based filtering automatically
    // No need to pass show_all, show_none, or management_company_id
    return filters;
  };

  // Modified query function to pass parameters and filters correctly
  const { data: managementCompaniesResponse, isLoading } = useQuery({
    queryKey: ["management-companies", searchParams, selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      const filters = getApiFilters();
      
      // Combine search params with role-based filters
      const params = {
        ...searchParams,
        ...filters
      };

      console.log("Management Companies API call with filters:", params);

      return managementCompanyApi.list({ 
        queryKey: ["management-companies", params]
      });
    },
    enabled: !!selectedRole && permissionsLoaded, // Only run query when we have a selected role AND permissions are loaded
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading management companies",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  // Handle different response formats - either a direct array or a paginated response
  let managementCompanies: ManagementCompany[] = [];
  let totalCount = 0;

  if (managementCompaniesResponse) {
    if (Array.isArray(managementCompaniesResponse)) {
      // Direct array response
      managementCompanies = managementCompaniesResponse;
      totalCount = managementCompaniesResponse.length;
    } else if (typeof managementCompaniesResponse === 'object' && 'data' in managementCompaniesResponse) {
      // Paginated response with data property
      const typedResponse = managementCompaniesResponse as { data: ManagementCompany[], meta?: { total_count: number } };
      managementCompanies = typedResponse.data || [];
      totalCount = typedResponse.meta?.total_count || managementCompanies.length;
    }
  }

  React.useEffect(() => {
    if (totalCount && searchParams.limit) {
      setTotalPages(Math.ceil(totalCount / searchParams.limit));
    }
  }, [totalCount, searchParams.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setSearchParams((prev) => ({ ...prev, search: searchTerm, offset: 0 }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams(prev => ({
      ...prev,
      offset: (page - 1) * (prev.limit || 10)
    }));
  };

  // Check if user can create management companies (only platform users with write permission)
  const canCreateManagementCompany = userRoles.platform.length > 0 && 
    selectedRole?.type === 'platform' && 
    hasPermission(getCurrentRoleIds(), 'write', 'management_companies');

  // Check if user can edit management companies
  const canEditManagementCompany = hasPermission(getCurrentRoleIds(), 'write', 'management_companies');

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-10">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-2xl font-bold flex items-center gap-2">
              <Building className="h-6 w-6" />
              Management Companies
            </div>
            <div className="flex items-center gap-2">
              {canCreateManagementCompany && (
                <Button asChild>
                  <Link to="/backoffice/management-companies/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Management Company
                  </Link>
                </Button>
              )}
            </div>
          </div>
          
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center gap-4">
            <Input
              type="search"
              placeholder="Search by name..."
              className="md:w-[300px]"
              onChange={handleSearchChange}
              value={searchParams.search}
            />
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!permissionsLoaded || isLoading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="flex justify-center items-center py-4 space-x-2">
                      <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
                      <span className="text-sm text-gray-600">Loading management companies...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : managementCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    {selectedRole?.type === 'management_company' 
                      ? "No management company found for your role"
                      : "No management companies found"}
                  </TableCell>
                </TableRow>
              ) : (
                managementCompanies.map((company) => {
                  return (
                    <TableRow key={company.id}>
                      <TableCell>
                        <div className="font-medium">{company.name}</div>
                      </TableCell>
                      <TableCell>{company.email || "-"}</TableCell>
                      <TableCell>{company.phone || "-"}</TableCell>
                      <TableCell>
                        {company.city && company.state 
                          ? `${company.city}, ${company.state}`
                          : company.city || company.state || "-"}
                      </TableCell>
                      
                        <TableCell className="text-right">
                          {canEditManagementCompany ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Open menu</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to={`/backoffice/management-companies/${company.id}/edit`}>
                                    <FilePen className="mr-2 h-4 w-4" />
                                    Edit
                                  </Link>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <span className="text-muted-foreground text-sm">No actions</span>
                          )}
                        </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {!isLoading && totalPages > 1 && (
          <div className="mt-4">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </BackofficeLayout>
  );
};

export default ManagementCompanies;
